// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { RouteGuardService } from '@services/route-guard.service';

export interface ComponentCanDeactivate {
    canDeactivate: () => boolean | Observable<boolean>;
}

@Injectable()
export class PendingChangesGuard implements CanDeactivate<ComponentCanDeactivate> {
    constructor(
        private readonly dialog: MatDialog,
        private readonly routeGuardService: RouteGuardService
    ) {}

    canDeactivate(
        component: ComponentCanDeactivate,
        currentRoute: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState: RouterStateSnapshot
    ): boolean | Observable<boolean> {
        // Since we switch routes between editing an f&i form and adding annotations
        // to an f&i form
        const formsOverride =
            (currentState.url === '/dashboard/forms' && nextState.url === '/dashboard/forms/edit') ||
            (nextState.url === '/dashboard/forms' && currentState.url === '/dashboard/forms/edit');

        const message = () => {
            if (currentState.url === '/dashboard/forms') {
                return 'You have made modifications to this F&I Form that have not been saved. If you do not want to keep them, click Confirm. Otherwise, click Cancel and continue modifying and / or save the form.';
            } else if (currentState.url === '/dashboard/custom') {
                return 'You have made modifications to this Custom Deal Type that have not been saved. If you do not want to keep them, click Confirm. Otherwise, click Cancel and continue modifying and / or save the deal type.';
            } else {
                return 'Press Cancel to go back, or Continue to lose your changes.';
            }
        };

        // if there are no pending changes, just allow deactivation; else confirm first
        const canActivateEvaluation =
            component.canDeactivate() || formsOverride
                ? true
                : this.dialog
                      .open(ConfirmModalComponent, {
                          width: '700px',
                          data: {
                              title: 'You Have Unsaved Changes',
                              message: message(),
                              btnText: 'Confirm',
                          },
                      })
                      .afterClosed();

        if (canActivateEvaluation === true) {
            this.routeGuardService.logRouteGuard(this.constructor.name, canActivateEvaluation);
        } else {
            canActivateEvaluation.subscribe((canActivate) => {
                this.routeGuardService.logRouteGuard(this.constructor.name, canActivate);
            });
        }

        return canActivateEvaluation;
    }
}
